import Button from '@/components/Button';
import Input from '@/components/Input';
import { FiChevronDown, FiInfo, FiLink, FiSearch, HiOutlineSparkles, TbCheck, TbHandStop, TbX } from '@/components/react-icons/main';
import Spinner from '@/components/Spinner';
import Tag from '@/components/Tag';
import IconV2 from '@/components/v2/IconV2';
import * as Popover from '@radix-ui/react-popover';
import { useEffect, useState } from 'react';

const CustomCheckbox = ({ checked, onChange }) => (
  <div className={`custom-checkbox ${checked ? 'checked' : ''}`} onClick={onChange}>
    <div className="checkbox-inner" />
  </div>
);

const Suggestion = ({ item, isSelected, handleToggleSuggestion, onStop, onReject, onAccept, type = 'default', showDiff = true, multiSelect = false }) => {
  const computedType = item?.suggestionType || type;

  return (
    <div className="suggestion" data-selected={isSelected} onClick={() => handleToggleSuggestion(item)}>
      <div className={'suggestion-inner-wrapper'}>
        <div className="suggestion-left">
          <div className="suggestion-info">
            <div className="suggestion-label">
              {item?.icon && <IconV2 icon={item?.icon} className={`suggestion-icon ${item?.iconClassName}`} />}
              {item?.title}
              {item?.tag && <Tag size="sm">{item?.tag}</Tag>}
              {item?.info && <IconV2 icon={FiInfo} size={12} title={item?.info} className="light-medium" />}
              {item?.docs && <Button className="ghost" size="sm" icon={FiLink} title="View docs" route={item?.docs} />}
            </div>
            <div className="suggestion-values">
              {computedType === 'default' && (
                <>
                  {showDiff && (
                    <div className="value-item strikethrough" data-title={item?.suggestion?.currentLabel || item?.suggestion?.currentValue || 'Unset'}>
                      {item?.suggestion?.currentLabel || item?.suggestion?.currentValue || 'Unset'}
                    </div>
                  )}
                  <div className="value-item" data-title={item?.suggestion?.suggestedLabel || item?.suggestion?.suggestedValue}>
                    {item?.suggestion?.suggestedLabel || item?.suggestion?.suggestedValue}
                  </div>
                </>
              )}
              {computedType === 'arguments' && (
                <div className="suggestion-keys">
                  <span>{item?.key}</span> &gt; <span>{item?.externalKey}</span>
                </div>
              )}
              {computedType === 'actions' && (
                <div className="suggestion-url">
                  <span>{item?.url}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="suggestion-right">
          {computedType === 'default' && !multiSelect && (
            <div className="suggestion-actions">
              <Button size="sm" className="ghost stop-suggestion" icon={TbHandStop} title="Don't suggest this again" onClick={() => onStop(item)} />
              <Button size="sm" className="ghost reject-suggestion" icon={TbX} title="Reject" onClick={() => onReject(item)} />
              <Button size="sm" className="ghost accept-suggestion" icon={TbCheck} title="Accept" onClick={() => onAccept(item)} />
            </div>
          )}
          {multiSelect && (
            <div className="suggestion-actions">
              <Button size="sm" className="ghost stop-suggestion" icon={TbHandStop} title="Don't suggest this again" onClick={() => onStop(item)} />
              {computedType === 'default' && <Button size="sm" className="ghost reject-suggestion" icon={TbX} title="Reject" onClick={() => onReject(item)} />}
              <CustomCheckbox checked={isSelected} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AiSuggestionsContent = ({ open = true, type = 'default', title, subtitle, emptyStateMessage, items, isLoading, headerDocs, side = 'right', height, sideOffset = 8, align = 'middle', showDiff = true, multiSelect = false, useSearch = false, onStop, onReject, onAccept, onMultiAccept, onStopAll, searchValue, onSearch, buttonLabel }) => {
  const [selectedSuggestions, setSelectedSuggestions] = useState(new Set());
  const [collapsedSections, setCollapsedSections] = useState(new Set());

  const hasSections = Array.isArray(items) && items.some((item) => item?.items);
  const allSuggestions = hasSections ? items.flatMap((item) => item?.items) : items;

  const allItemsSelected = (item) => {
    return item?.items?.every((n) => selectedSuggestions.has(n.key));
  };

  const allItemsUnselected = (item) => {
    return item?.items?.every((n) => !selectedSuggestions.has(n.key));
  };

  const isSectionCollapsed = (item) => {
    return collapsedSections.has(item?.title) || !item?.items?.length;
  };

  const handleToggleSuggestion = (suggestion) => {
    setSelectedSuggestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(suggestion.key)) {
        newSet.delete(suggestion.key);
      } else {
        newSet.add(suggestion.key);
      }
      return newSet;
    });
  };

  const handleToggleSection = (sectionTitle) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionTitle)) {
        newSet.delete(sectionTitle);
      } else {
        newSet.add(sectionTitle);
      }
      return newSet;
    });
  };

  const handleSelectAll = (item) => {
    setSelectedSuggestions((prev) => {
      const newSet = new Set(prev);
      item?.items?.forEach((item) => newSet.add(item.key));
      return newSet;
    });
  };

  const handleClear = (item) => {
    setSelectedSuggestions((prev) => {
      const newSet = new Set(prev);
      item?.items?.forEach((item) => newSet.delete(item.key));
      return newSet;
    });
  };

  const handleMultiAccept = () => {
    const selectedItems = allSuggestions.filter((item) => selectedSuggestions.has(item?.key));
    onMultiAccept(selectedItems);
  };

  useEffect(() => {
    if (!open) {
      setSelectedSuggestions(new Set());
      setCollapsedSections(new Set());
    } else {
      setSelectedSuggestions(new Set());
    }
  }, [open]);

  return (
    <Popover.Portal>
      <Popover.Content side={side} sideOffset={sideOffset} align={align} className={`comp-ai-suggestions-content`} onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="header">
          <IconV2 icon={HiOutlineSparkles} className="header-icon" />
          <div className="header-content">
            <div className="header-title">{title}</div>
            {subtitle && <div className="header-subtitle">{subtitle}</div>}
          </div>
          {(!!isLoading || !!headerDocs) && (
            <div className="header-actions">
              {isLoading && <Spinner size={8} />}
              {headerDocs && <Button className="ghost" size="sm" icon={FiLink} title="View docs" route={headerDocs} />}
            </div>
          )}
        </div>
        <div className="suggestions-container" data-has-sections={hasSections} style={{ height }}>
          {useSearch && (
            <div className="search-container">
              <Input icon={FiSearch} size="sm" iconPosition="left" placeholder="Search" value={searchValue} onChange={onSearch} autoFocus />
            </div>
          )}
          {hasSections && (
            <>
              {items?.map((item, index) => (
                <div className={`suggestion-section ${isSectionCollapsed(item) ? 'collapsed' : ''}`} key={index}>
                  <div className="suggestion-section-title">
                    <div className="left" onClick={() => handleToggleSection(item?.title)}>
                      <span className={'label'}>{item?.title}</span>
                      <IconV2 icon={FiChevronDown} className={`section-chevron ${isSectionCollapsed(item) ? 'collapsed' : ''}`} />
                    </div>
                    {!isSectionCollapsed(item) && (
                      <div className="right">
                        <Button disabled={allItemsSelected(item)} size="xs" className={`${allItemsSelected(item) ? 'text' : 'link'} slim`} onClick={() => handleSelectAll(item)}>
                          Select All
                        </Button>
                        <Button disabled={allItemsUnselected(item)} size="xs" className={`${allItemsUnselected(item) ? 'text' : 'link'} slim`} onClick={() => handleClear(item)}>
                          Clear
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className={`suggestion-section-content ${isSectionCollapsed(item) ? 'collapsed' : ''}`}>
                    {item?.items?.map((n, idx) => (
                      <Suggestion key={idx} item={n} isSelected={selectedSuggestions.has(n?.key)} showDiff={showDiff} handleToggleSuggestion={handleToggleSuggestion} onStop={onStop} onReject={onReject} onAccept={onAccept} type={type} multiSelect={multiSelect} />
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
          {!hasSections && (
            <>
              {items?.map((item, index) => (
                <Suggestion key={index} item={item} isSelected={selectedSuggestions.has(item?.key)} handleToggleSuggestion={handleToggleSuggestion} showDiff={showDiff} onStop={onStop} onReject={onReject} onAccept={onAccept} type={type} multiSelect={multiSelect} />
              ))}
            </>
          )}

          {items?.length === 0 && <div className="no-suggestions">{emptyStateMessage}</div>}
        </div>
        {multiSelect && (
          <div className="footer">
            <div className="footer-left">{allSuggestions?.length > 0 && <Button size="sm" className="ghost stop-suggestion" icon={TbHandStop} title="Don't suggest arguments for this action again" onClick={onStopAll} />}</div>
            <div className="footer-right">
              <Button size="sm" className="primary" onClick={handleMultiAccept} disabled={selectedSuggestions.size === 0}>
                {buttonLabel}
                {!buttonLabel && (
                  <>
                    Create {selectedSuggestions.size} {type === 'arguments' ? 'Argument' : 'Action'}
                    {selectedSuggestions.size === 1 ? '' : 's'}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Popover.Content>
    </Popover.Portal>
  );
};

export default AiSuggestionsContent;
